import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Skeleton from "react-loading-skeleton";
import { useLocation, useParams } from "react-router-dom";
import styled from "styled-components";
import { paths } from "pages/App/helpers/paths";
import { questionTypes } from "pages/App/helpers/questionTypes";
import QuestionsTable from "pages/PanelPage/components/selectedAssessment/assessmentResults/takerResults/questionsTable/QuestionsTable";
import { getAssessmentAttemptMW } from "pages/PanelPage/middleware";
import { FlexContainer } from "components/Atoms/Containers";
import { Body2 } from "components/Atoms/Text";
import clipboardCheckIcon from "assets/clipboardCheckIcon";
import externalLinkIcon from "assets/externalLinkIcon";

// Returns true if a section has multiple choice question results,
// used to point TAs to the MCQ score report when a section has no
// results for the type currently being viewed
const sectionHasMcqResults = section =>
  section?.slot_attempts?.some(
    attempt => attempt.question_type === questionTypes.SELECT
  );

// Component to display filtered results of a specific assessment attempt
// filtered by question type
const TakerResultsbyType = ({ type, isLoading }) => {
  const { assessment, attemptUUID } = useParams();
  const { search } = useLocation();
  const publicUuid = new URLSearchParams(search).get("public_uuid");
  const mcqReportLink = `${
    paths.ADMIN
  }/${assessment}/results/${attemptUUID}/mcq_results${
    publicUuid ? `?public_uuid=${publicUuid}` : ""
  }`;
  const [isLoadingAttempt, setIsLoading] = useState(isLoading);
  const [sections, setSections] = useState([]);
  const [filteredSlotAttempts, setFilteredSlotAttempts] = useState([]);
  useEffect(() => {
    // Fetchs the assessment attempt and filters the sections
    // by question type
    const fetchAssessmentAttempt = async () => {
      const takerData = await getAssessmentAttemptMW({
        urlParams: [attemptUUID]
      });

      // Initialize arrays to accumulate results
      let allSections = [];
      let allFilteredSlotAttempts = [];

      // Use Promise.all to wait for all async operations in forEach
      await Promise.all(
        takerData.data?.section_attempts?.map(async section => {
          allSections.push(section);
          // Filter the slot attempts by question type
          const takerfilteredSections = section?.slot_attempts?.filter(
            attempt => {
              return attempt.question_type === type;
            }
          );
          allFilteredSlotAttempts.push(takerfilteredSections);
        })
      );

      // Update state outside the loop
      setSections(allSections);
      setFilteredSlotAttempts(allFilteredSlotAttempts);
      setIsLoading(false);
    };

    fetchAssessmentAttempt();
  }, [attemptUUID]);

  return (
    <>
      {isLoadingAttempt && (
        <FlexContainer data-testid={"skeleton"} direction={"column"} gap={"10"}>
          <Skeleton height={"15em"} />
          <Skeleton height={"15em"} />
          <Skeleton height={"15em"} />
        </FlexContainer>
      )}
      {!isLoadingAttempt &&
        sections.length > 0 &&
        filteredSlotAttempts.length === 0 && <p>No results</p>}
      {!isLoadingAttempt && filteredSlotAttempts.length > 0 && (
        <FlexContainer gap={"16"} direction={"column"}>
          {filteredSlotAttempts.map((section, index) => {
            return (
              <>
                <span>{sections[index].name}</span>
                {section.length > 0 && (
                  <QuestionsTable
                    slotAttempts={section}
                    hideAllQuestions
                    key={sections[index].id}
                  />
                )}
                {section.length === 0 && sectionHasMcqResults(sections[index]) && (
                  <ScoreReportNotice
                    align="center"
                    justify="space-between"
                    gap="16"
                    key={sections[index].id}
                  >
                    <FlexContainer align="center" gap="16">
                      <IconWrapper>{clipboardCheckIcon}</IconWrapper>
                      <Body2>
                        This section was <strong>auto-scored</strong>. View the
                        report to see the learner&apos;s selected answers.
                      </Body2>
                    </FlexContainer>
                    <ScoreReportLink
                      href={mcqReportLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View score report {externalLinkIcon}
                    </ScoreReportLink>
                  </ScoreReportNotice>
                )}
                {section.length === 0 &&
                  !sectionHasMcqResults(sections[index]) && (
                    <p key={sections[index].id}>
                      This section has no results for this type of response
                    </p>
                  )}
              </>
            );
          })}
        </FlexContainer>
      )}
    </>
  );
};

TakerResultsbyType.propTypes = {
  type: PropTypes.oneOf(Object.values(questionTypes)).isRequired,
  isLoading: PropTypes.bool
};

TakerResultsbyType.defaultProps = {
  isLoading: false
};

export default TakerResultsbyType;

const ScoreReportNotice = styled(FlexContainer)`
  background: ${({ theme }) => theme.colors.grey};
  border-radius: 8px;
  padding: 16px 24px;
`;

const IconWrapper = styled.span`
  color: ${({ theme }) => theme.tenantAccent};
  display: inline-flex;
  flex-shrink: 0;
`;

const ScoreReportLink = styled.a`
  align-items: center;
  border: 1.5px solid ${({ theme }) => theme.tenantAccent};
  border-radius: 4px;
  color: ${({ theme }) => theme.tenantAccent};
  display: inline-flex;
  flex-shrink: 0;
  font-family: ${({ theme }) => theme.fonts.avenirProHeavy};
  font-size: 14px;
  font-weight: 900;
  gap: 8px;
  padding: 10px 20px;
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    background: ${({ theme }) => theme.tenantAccent};
    color: ${({ theme }) => theme.colors.white};
  }
`;
